import AddToCartButton from "~/components/product/AddToCartButton"
import { useOffer } from "~/sdk/useOffer"
import type { Product, ProductDetailsPage } from "@decocms/apps-commerce/types"
import Image from "~/components/ui/Image"
import Title from "~/components/ui/Title"
import { TitleIcon } from "~/loaders/TitleIcon"
import clsx from "~/sdk/clx"
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import { toProductItem } from "~/sdk/ga4/transform/toProductItem"
import { SendEventOnView } from "~/components/Analytics"
import { useId } from "react"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"

export interface Props {
    page: ProductDetailsPage | null
    products: Product[] | null
    /**
     * @title Título
     * @format html
     */
    title?: string
    /**
     * @title Ícone
     */
    titleIcon: TitleIcon
}

const width = 275
const height = 275

interface ProductCardProps {
    name: string
    image: string
    url: string
    border?: boolean
}

function ProductCard({ name, image, url, border }: ProductCardProps) {
    return (
        <a
            className={clsx('py-10 text-center w-full', border && 'border-2 border-[#cccccc]')}
            href={url}
            style={{ maxWidth: width }}
        >
            <Image src={image} alt={name} width={width} height={height} loading='lazy' />
            <p className='text-sm text-neutral-600 font-bold mt-2'>{name}</p>
        </a>
    )
}

function useProductsInstallment(...products: Product[]) {
    const offers = products.map((product) => useOffer(product.offers))

    const billingDuration = offers.reduce((acc, offer) => {
        const installment = offer.bestInstallment?.billingDuration ?? 0
        return Math.min(acc, installment)
    }, Infinity)

    const price = offers.reduce((acc, offer) => {
        const installment = offer.bestInstallment?.price ?? 0
        return acc + installment
    }, 0)

    const billingPrice = formatPrice(price / billingDuration)!

    const withTaxes = offers.reduce((acc, offer) => {
        const offerPrice: number = offer.price ?? 0
        return acc + offerPrice
    }, 0) < price

    return {
        price: formatPrice(price)!,
        billingDuration,
        billingPrice,
        withTaxes,
    }
}

export default function BuyTogether({ page, products, title, titleIcon }: Props) {
    if (!page || !products || products.length === 0) {
        return <></>
    }

    const id = useId()
    const { product: mainProduct } = page
    const [buyTogetherProduct] = products

    const mainProductOffer = useOffer(mainProduct.offers)
    const buyTogetherProductOffer = useOffer(buyTogetherProduct.offers)

    if (
        mainProductOffer.availability !== 'https://schema.org/InStock' ||
        buyTogetherProductOffer.availability !== 'https://schema.org/InStock'
    ) {
        return null
    }

    const { price, billingDuration, billingPrice, withTaxes } = useProductsInstallment(
        mainProduct,
        buyTogetherProduct,
    )

    const analytics = {
        items: [
            toProductItem(mainProduct, { index: 0, quantity: 1 }),
            toProductItem(buyTogetherProduct, { index: 1, quantity: 1 }),
        ],
        view: { id: 'buy_together', name: 'Compre junto' },
    }

    const viewItemListEvent = toAnalytics({
        type: 'view_item_list',
        data: {
            items: [mainProduct, buyTogetherProduct].map((product, index) =>
                toProductItem(product, { quantity: 1, index })
            ),
            view: {
                id: 'buy_together',
                name: 'Compre junto',
            },
        },
    })

    return (
        <div id={id} className='w-full container py-8 flex flex-col gap-12 lg:gap-16 lg:py-10'>
            <div className='flex items-center justify-center gap-3 px-3'>
                {title && <Title title={title} icon={titleIcon} />}
            </div>

            <div className='flex justify-center items-center flex-col md:flex-row md:gap-6 lg:gap-10 px-3'>
                <ProductCard
                    name={mainProduct.name ?? mainProduct.isVariantOf?.name ?? ''}
                    image={mainProduct.image?.[0]?.url ?? ''}
                    url={mainProduct.isVariantOf?.url ?? mainProduct.url ?? '#'}
                    border
                />
                <span className='text-salmon text-5xl'>+</span>
                <ProductCard
                    name={buyTogetherProduct.name ?? buyTogetherProduct.isVariantOf?.name ?? ''}
                    image={buyTogetherProduct.image?.[0]?.url ?? ''}
                    url={buyTogetherProduct.isVariantOf?.url ?? buyTogetherProduct.url ?? '#'}
                />
                <span className='text-salmon text-5xl'>=</span>
                <div className='text-center w-[232px] space-y-3'>
                    <p className='text-2xl text-black uppercase leading-[100%]'>
                        Compre os 2 itens por
                    </p>
                    <div>
                        <p className='text-2xl font-bold text-salmon'>{price}</p>
                        <p className='text-sm text-neutral-600'>
                            ou até {billingDuration}x de {billingPrice}{' '}
                            {withTaxes ? 'com juros' : 'sem juros'}
                        </p>
                    </div>
                    <AddToCartButton
                        className='min-h-0 p-0 font-bold text-white border-2 text-sm border-none rounded btn bg-salmon hover:bg-salmon hover:border-none w-full mt-3'
                        text='COMPRAR OS 2 PRODUTOS'
                        products={[
                            {
                                id: mainProduct.productID,
                                seller: mainProductOffer.seller ?? '1',
                                quantity: 1,
                            },
                            {
                                id: buyTogetherProduct.productID,
                                seller: buyTogetherProductOffer.seller ?? '1',
                                quantity: 1,
                            },
                        ]}
                        analytics={analytics}
                    />
                </div>
            </div>

            <SendEventOnView id={id} event={viewItemListEvent} />
        </div>
    )
}
